import React from 'react'
import { Pane, Text, majorScale, Card, Button, IconButton, Spinner, Icon } from 'evergreen-ui'
import { Link } from 'react-router'
import { settingsNotificationsPath } from 'lib/path-helpers/workspaces'
import { NotificationProps } from './Notification'
import NotificationsGroup from './NotificationsGroup'
import CircleIconWrapper from 'components/Icons/CircleIconWrapper'

// @ts-ignore ts in editors don't always seem to know how css imports work
import styles from './Bell.module.css'

function CenteredPane(props) {
  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={majorScale(4)}
      background="white"
      {...props}
    />
  )
}

interface NotificationCenterProps {
  actionItems: NotificationProps[]
  updateItems: any[]
  onNotificationClick: (item: NotificationProps) => void
  onLoadMore: () => any
  close: () => void
  hasError: boolean
  hasNextPage: boolean
  isLoading: boolean
  workspaceSlug: string
}

class NotificationCenter extends React.Component<NotificationCenterProps> {
  renderEmptyState() {
    return (
      <CenteredPane>
        <CircleIconWrapper
          className={styles.bell}
          color="neutral"
          icon={<Icon icon="notifications" size={majorScale(3)} transform="rotate(12deg)" />}
          marginTop={0}
        />
        <Text>You're all caught up! No new notifications.</Text>
      </CenteredPane>
    )
  }

  renderErrorState() {
    return (
      <CenteredPane>
        <Text>There was an issue loading your notifications.</Text>
      </CenteredPane>
    )
  }

  renderLoadingState() {
    return (
      <CenteredPane>
        <Spinner />
      </CenteredPane>
    )
  }

  renderNotifications() {
    const { actionItems, hasNextPage, onLoadMore, onNotificationClick, updateItems } = this.props

    return (
      <React.Fragment>
        {actionItems.length > 0 && (
          <NotificationsGroup
            id="action-items"
            title={`Action Items (${actionItems.length})`}
            intent="danger"
            items={actionItems}
            onNotificationClick={onNotificationClick}
          />
        )}

        {updateItems.length > 0 && (
          <NotificationsGroup
            id="updates"
            title="Updates"
            items={updateItems}
            onNotificationClick={onNotificationClick}
          />
        )}

        {hasNextPage && (
          <Button width="100%" onClick={onLoadMore}>
            Load More
          </Button>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { actionItems, hasError, isLoading, updateItems, workspaceSlug } = this.props
    const isEmpty = actionItems.length === 0 && updateItems.length === 0

    let content: any = null
    if (isLoading) {
      content = this.renderLoadingState()
    } else if (hasError) {
      content = this.renderErrorState()
    } else if (isEmpty) {
      content = this.renderEmptyState()
    } else {
      content = this.renderNotifications()
    }

    return (
      <Card border="muted" background="tint1" maxWidth={500} elevation={3}>
        <Pane
          padding={majorScale(1)}
          paddingLeft={majorScale(2)}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          borderBottom
          // To overlap the bottom border and top border of the first group
          marginBottom={-1}
        >
          <Text>Notifications</Text>
          <Link to={settingsNotificationsPath({ workspaceSlug })}>
            <IconButton icon="cog" onClick={this.props.close} />
          </Link>
        </Pane>

        <Pane minHeight={majorScale(10)} maxHeight="calc(90vh - 150px)" overflow="auto">
          {content}
        </Pane>
      </Card>
    )
  }
}

export default NotificationCenter
